/**
 * Insurance Forms Settings Component
 * Admin interface for managing health insurance form templates
 * Includes PDF coordinate adjustment, test download, and signature pad
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { FileText, Upload, Trash2, Calendar, Image, AlertCircle, Check, Settings, Download, Move, Pen, RotateCcw } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Default coordinates (matching backend)
const DEFAULT_COORDS = {
  insurance_name_x: 250,
  insurance_name_y: 530,
  supplementary_insurance_x: 250,
  supplementary_insurance_y: 496,
  insurance_number_x: 250,
  insurance_number_y: 513,
  customer_name_x: 250,
  customer_name_y: 479,
  street_x: 250,
  street_y: 462,
  plz_city_x: 250,
  plz_city_y: 445,
  date_x: 120,
  date_y: 165,
  signature_x: 380,
  signature_y: 130,
  stamp_x: 480,
  stamp_y: 115,
  font_size: 11
};

export default function InsuranceFormsSettings() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({});
  const [coordinates, setCoordinates] = useState(DEFAULT_COORDS);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showSignatureDialog, setShowSignatureDialog] = useState(false);
  const [uploadData, setUploadData] = useState({
    year: new Date().getFullYear(),
    name: '',
    description: '',
    file: null
  });
  const [uploading, setUploading] = useState(false);
  const [testYear, setTestYear] = useState(new Date().getFullYear());
  const [downloading, setDownloading] = useState(false);
  
  // Signature Pad
  const signaturePadRef = useRef(null);

  const loadTemplates = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/insurance/admin/templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(response.data);
    } catch (error) {
      console.error('Error loading templates:', error);
      toast.error('Fehler beim Laden der Vorlagen');
    }
  }, []);

  const loadSettings = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/insurance/admin/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(response.data || {});
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  }, []);

  const loadCoordinates = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/insurance/admin/settings/coordinates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCoordinates({ ...DEFAULT_COORDS, ...response.data });
    } catch (error) {
      console.error('Error loading coordinates:', error);
    }
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([loadTemplates(), loadSettings(), loadCoordinates()]);
      setLoading(false);
    };
    load();
  }, [loadTemplates, loadSettings, loadCoordinates]);

  const handleUploadTemplate = async () => {
    if (!uploadData.file || !uploadData.name || !uploadData.year) {
      toast.error('Bitte alle Felder ausfüllen');
      return;
    }

    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('year', uploadData.year);
      formData.append('name', uploadData.name);
      formData.append('description', uploadData.description || '');
      formData.append('file', uploadData.file);

      await axios.post(`${API}/insurance/admin/templates`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Vorlage erfolgreich hochgeladen');
      setShowUploadDialog(false);
      setUploadData({ year: new Date().getFullYear(), name: '', description: '', file: null });
      loadTemplates();
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Hochladen');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteTemplate = async (templateId, year) => {
    if (!window.confirm(`Vorlage für ${year} wirklich löschen?`)) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/insurance/admin/templates/${templateId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Vorlage gelöscht');
      loadTemplates();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Fehler beim Löschen');
    }
  };

  const handleSaveSignature = async () => {
    if (!signaturePadRef.current || signaturePadRef.current.isEmpty()) {
      toast.error('Bitte unterschreiben Sie zuerst');
      return;
    }

    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Get the canvas directly - transparent background
      const canvas = signaturePadRef.current.getCanvas();
      const signatureDataUrl = canvas.toDataURL('image/png');
      
      console.log('Signature data length:', signatureDataUrl.length);
      
      const response = await axios.post(`${API}/insurance/admin/settings/signature-data`, {
        signature_data: signatureDataUrl
      }, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.success) {
        toast.success('Unterschrift gespeichert');
        setShowSignatureDialog(false);
        loadSettings();
      } else {
        toast.error(response.data.detail || 'Fehler beim Speichern');
      }
    } catch (error) {
      console.error('Upload error:', error);
      const errorMsg = error.response?.data?.detail || 'Fehler beim Speichern der Unterschrift';
      toast.error(errorMsg);
    } finally {
      setUploading(false);
    }
  };

  const handleClearSignature = () => {
    if (signaturePadRef.current) {
      signaturePadRef.current.clear();
    }
  };

  const handleSaveCoordinates = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/insurance/admin/settings/coordinates`, coordinates, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Koordinaten gespeichert');
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Fehler beim Speichern');
    }
  };

  const handleTestDownload = async () => {
    if (templates.length === 0) {
      toast.error('Bitte erst eine Vorlage hochladen');
      return;
    }

    setDownloading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/insurance/admin/test-download/${testYear}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success && response.data.pdf_base64) {
        // Convert base64 to blob and download
        const byteCharacters = atob(response.data.pdf_base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });
        
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = response.data.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        toast.success('Test-PDF heruntergeladen');
      }
    } catch (error) {
      console.error('Download error:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Herunterladen');
    } finally {
      setDownloading(false);
    }
  };

  const CoordInput = ({ label, xKey, yKey }) => (
    <div className="grid grid-cols-3 gap-2 items-center">
      <Label className="text-sm text-gray-600">{label}</Label>
      <div className="flex items-center gap-1">
        <span className="text-xs text-gray-400">X:</span>
        <Input
          type="number"
          className="h-8 text-sm"
          value={coordinates[xKey]}
          onChange={(e) => setCoordinates({ ...coordinates, [xKey]: parseInt(e.target.value) || 0 })}
        />
      </div>
      <div className="flex items-center gap-1">
        <span className="text-xs text-gray-400">Y:</span>
        <Input
          type="number"
          className="h-8 text-sm"
          value={coordinates[yKey]}
          onChange={(e) => setCoordinates({ ...coordinates, [yKey]: parseInt(e.target.value) || 0 })}
        />
      </div>
    </div>
  );

  if (loading) {
    return (
      <Card>
        <CardContent className="p-8 flex justify-center">
          <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Tabs defaultValue="templates" className="space-y-4">
      <TabsList>
        <TabsTrigger value="templates" className="gap-2">
          <FileText size={16} />
          Vorlagen
        </TabsTrigger>
        <TabsTrigger value="coordinates" className="gap-2">
          <Move size={16} />
          Positionen
        </TabsTrigger>
      </TabsList>

      {/* Templates Tab */}
      <TabsContent value="templates" className="space-y-4">
        {/* Header Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-blue-600" />
              Krankenkassenformulare
            </CardTitle>
            <CardDescription>
              Verwalten Sie hier die Krankenkassenformulare für Ihre Kunden. 
              Kunden können ihre ausgefüllten Formulare herunterladen, sobald sie mindestens 10 Trainings absolviert haben.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2">
              <Button onClick={() => setShowUploadDialog(true)}>
                <Upload className="h-4 w-4 mr-2" />
                Neue Vorlage hochladen
              </Button>
              <Button variant="outline" onClick={() => setShowSignatureDialog(true)}>
                <Pen className="h-4 w-4 mr-2" />
                Unterschrift zeichnen
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Signature Status */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Unterschrift/Stempel Status</CardTitle>
          </CardHeader>
          <CardContent>
            {settings.signature_filepath ? (
              <div className="flex items-center gap-2 text-green-600">
                <Check className="h-5 w-5" />
                <span>Unterschrift/Stempel ist hinterlegt</span>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-amber-600">
                <AlertCircle className="h-5 w-5" />
                <span>Noch keine Unterschrift/Stempel hinterlegt</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Templates List */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Hochgeladene Vorlagen</CardTitle>
          </CardHeader>
          <CardContent>
            {templates.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>Noch keine Vorlagen hochgeladen</p>
                <p className="text-sm">Laden Sie Ihr erstes Krankenkassenformular hoch</p>
              </div>
            ) : (
              <div className="space-y-3">
                {templates.map((template) => (
                  <div
                    key={template.id}
                    className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50"
                  >
                    <div className="flex items-center gap-4">
                      <div className="h-12 w-12 rounded-lg bg-blue-50 flex items-center justify-center">
                        <FileText className="h-6 w-6 text-blue-600" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{template.name}</span>
                          <Badge variant="secondary">
                            <Calendar className="h-3 w-3 mr-1" />
                            {template.year}
                          </Badge>
                        </div>
                        {template.description && (
                          <p className="text-sm text-gray-500">{template.description}</p>
                        )}
                        <p className="text-xs text-gray-400 mt-1">
                          Hochgeladen: {new Date(template.created_at).toLocaleDateString('de-CH')}
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      onClick={() => handleDeleteTemplate(template.id, template.year)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </TabsContent>

      {/* Coordinates Tab */}
      <TabsContent value="coordinates" className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Move className="h-5 w-5 text-purple-600" />
              PDF-Positionen anpassen
            </CardTitle>
            <CardDescription>
              Passen Sie die Position der Textfelder auf dem PDF an. 
              X = Abstand von links, Y = Abstand von unten (A4 = 595 x 842 Punkte).
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-3">
              <CoordInput label="Krankenkasse" xKey="insurance_name_x" yKey="insurance_name_y" />
              <CoordInput label="Versichertennr." xKey="insurance_number_x" yKey="insurance_number_y" />
              <CoordInput label="Zusatzvers.nr." xKey="supplementary_insurance_x" yKey="supplementary_insurance_y" />
              <CoordInput label="Name/Vorname" xKey="customer_name_x" yKey="customer_name_y" />
              <CoordInput label="Strasse" xKey="street_x" yKey="street_y" />
              <CoordInput label="PLZ/Ort" xKey="plz_city_x" yKey="plz_city_y" />
              <CoordInput label="Datum" xKey="date_x" yKey="date_y" />
              <CoordInput label="Unterschrift" xKey="signature_x" yKey="signature_y" />
              <CoordInput label="Stempel" xKey="stamp_x" yKey="stamp_y" />
              
              <div className="grid grid-cols-3 gap-2 items-center pt-2 border-t">
                <Label className="text-sm text-gray-600">Schriftgrösse</Label>
                <div className="col-span-2 flex items-center gap-1">
                  <Input
                    type="number"
                    className="h-8 text-sm w-20"
                    value={coordinates.font_size}
                    onChange={(e) => setCoordinates({ ...coordinates, font_size: parseInt(e.target.value) || 11 })}
                  />
                  <span className="text-xs text-gray-400">pt</span>
                </div>
              </div>
            </div>
            
            <div className="flex gap-2 pt-4">
              <Button onClick={handleSaveCoordinates}>
                <Check className="h-4 w-4 mr-2" />
                Speichern
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Test Download */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Download className="h-5 w-5 text-green-600" />
              Test-Download
            </CardTitle>
            <CardDescription>
              Laden Sie ein Test-PDF mit Beispieldaten herunter, um die Positionen zu prüfen.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Label>Jahr:</Label>
                <select
                  className="border rounded-md px-3 py-2 text-sm"
                  value={testYear}
                  onChange={(e) => setTestYear(parseInt(e.target.value))}
                >
                  {templates.map((t) => (
                    <option key={t.year} value={t.year}>{t.year}</option>
                  ))}
                </select>
              </div>
              <Button 
                onClick={handleTestDownload} 
                disabled={downloading || templates.length === 0}
                variant="outline"
              >
                <Download className="h-4 w-4 mr-2" />
                {downloading ? 'Wird erstellt...' : 'Test-PDF herunterladen'}
              </Button>
            </div>
            {templates.length === 0 && (
              <p className="text-sm text-amber-600 mt-2">
                Bitte laden Sie erst eine Vorlage hoch, um den Test-Download zu nutzen.
              </p>
            )}
          </CardContent>
        </Card>
      </TabsContent>

      {/* Upload Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Neue Formularvorlage hochladen</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Jahr *</Label>
              <Input
                type="number"
                min="2020"
                max="2050"
                value={uploadData.year}
                onChange={(e) => setUploadData({ ...uploadData, year: parseInt(e.target.value) })}
              />
            </div>
            <div>
              <Label>Name *</Label>
              <Input
                placeholder="z.B. Abonnementbestätigung 2025"
                value={uploadData.name}
                onChange={(e) => setUploadData({ ...uploadData, name: e.target.value })}
              />
            </div>
            <div>
              <Label>Beschreibung (optional)</Label>
              <Input
                placeholder="Optionale Beschreibung"
                value={uploadData.description}
                onChange={(e) => setUploadData({ ...uploadData, description: e.target.value })}
              />
            </div>
            <div>
              <Label>PDF-Datei *</Label>
              <Input
                type="file"
                accept=".pdf"
                onChange={(e) => setUploadData({ ...uploadData, file: e.target.files[0] })}
              />
              <p className="text-xs text-gray-500 mt-1">Nur PDF-Dateien erlaubt</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadDialog(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleUploadTemplate} disabled={uploading}>
              {uploading ? 'Wird hochgeladen...' : 'Hochladen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Signature Pad Dialog */}
      <Dialog open={showSignatureDialog} onOpenChange={setShowSignatureDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pen className="h-5 w-5" />
              Unterschrift erstellen
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-gray-600">
              Unterschreiben Sie direkt im Feld unten (ideal für iPad/Tablet). 
              Diese Unterschrift wird automatisch auf alle generierten Formulare gesetzt.
            </p>
            
            {/* Signature Canvas */}
            <div className="border-2 border-dashed border-gray-300 rounded-lg overflow-hidden bg-white">
              <SignatureCanvas
                ref={signaturePadRef}
                penColor="black"
                canvasProps={{
                  width: 450,
                  height: 200,
                  className: 'signature-canvas w-full',
                  style: { touchAction: 'none' }
                }}
              />
            </div>
            
            <div className="flex justify-center">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleClearSignature}
                className="gap-2"
              >
                <RotateCcw className="h-4 w-4" />
                Löschen & Neu zeichnen
              </Button>
            </div>
            
            <p className="text-xs text-gray-500 text-center">
              Tipp: Nutzen Sie einen Stylus oder Ihren Finger für optimale Ergebnisse
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSignatureDialog(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleSaveSignature} disabled={uploading}>
              {uploading ? 'Wird gespeichert...' : 'Unterschrift speichern'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Tabs>
  );
}
